import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false); // Tracks if a login attempt is in progress
  const { login, currentUser, userRole, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (authLoading && !isLoggingIn) {
      // If Firebase auth is still initializing its state (and not from a form submission), wait.
      // This prevents premature actions if the user is already logged in from a previous session.
      return;
    }

    if (currentUser && userRole) {
      // User is authenticated and role is known, proceed with navigation.
      if (isLoggingIn || (!isLoggingIn && window.location.pathname === '/login')) {
        // Only navigate if a login was just attempted OR if user is on /login page and already authenticated.
        // This prevents re-navigation if user is on another page and auth state updates for other reasons.
        if (userRole === 'admin' || userRole === 'owner') {
          navigate('/admin-dashboard');
        } else {
          navigate('/'); // Or role-specific dashboard
        }
        if (isLoggingIn) {
          setIsLoggingIn(false); // Reset submission flag after navigation is initiated
        }
      }
    } else if (isLoggingIn && currentUser && !userRole) {
      // Login submitted, user authenticated, but role is still loading. Keep UI in "Logging in..."
      // No action needed here, useEffect will re-run when userRole updates.
    } else if (isLoggingIn && !currentUser) {
      // Login submitted, but resulted in no currentUser (e.g., bad credentials).
      // Error should be set by handleSubmit. Reset isLoggingIn to re-enable form.
      setIsLoggingIn(false);
    }
  }, [currentUser, userRole, authLoading, navigate, isLoggingIn]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoggingIn(true);
    try {
      await login(email, password);
      // Navigation is handled by useEffect.
    } catch (err) {
      setError('Failed to log in. Please check your credentials.');
      console.error("Login page handleSubmit error:", err);
      setIsLoggingIn(false);
    }
  };

  // If auth is initially loading and no login attempt is active, show a generic loading message.
  if (authLoading && !isLoggingIn && !currentUser) {
    return <div>Loading authentication state...</div>;
  }

  // If user is fully authenticated and role is known, and we are on the login page,
  // show a redirecting message instead of the form.
  if (currentUser && userRole && window.location.pathname === '/login') {
     // This state means useEffect should be handling navigation shortly.
    return <div>Login successful. Redirecting...</div>;
  }

  // If user is already logged in (currentUser exists) but somehow landed on LoginPage
  // and it's not due to an active login attempt (isLoggingIn is false),
  // this might indicate they manually navigated to /login. The useEffect should redirect them.
  // In this brief moment before useEffect navigates, we can show a less confusing message or nothing.
  // For now, if they are logged in but not actively logging in via form, and on /login, the above handles it.
  // If they are logged in and on /login, but role is not yet known, authLoading or isLoggingIn should cover it.

  return (
    // Use CSS variables for background color to respect theme changes
    <div className="flex items-center justify-center min-h-screen" style={{ backgroundColor: 'var(--color-bg)' }}>
      {/* The card will have its own background color, creating contrast */}
      <div 
        className="w-full max-w-md p-8 space-y-8 rounded-lg shadow-lg"
        style={{ backgroundColor: 'var(--color-card-bg)' }}
      >
        <h2 className="text-2xl font-bold text-center" style={{ color: 'var(--color-text)' }}>Login</h2>
        {error && (
          <p className="p-3 text-sm text-center text-red-700 bg-red-100 rounded-lg">
            {error}
          </p>
        )}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="flex items-center space-x-4">
            <label
              htmlFor="email"
              className="w-1/4 text-sm font-medium text-right"
              style={{ color: 'var(--color-text)' }}
            >
              Email:
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={isLoggingIn || (authLoading && !currentUser) || (currentUser && !userRole && isLoggingIn)}
              className="w-3/4 px-3 py-2 mt-1 border rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              style={{ 
                backgroundColor: 'var(--color-table-row-alt)', 
                color: 'var(--color-text)',
                borderColor: 'var(--color-table)'
              }}
            />
          </div>
          <div className="flex items-center space-x-4">
            <label
              htmlFor="password"
              className="w-1/4 text-sm font-medium text-right"
              style={{ color: 'var(--color-text)' }}
            >
              Password:
            </label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={isLoggingIn || (authLoading && !currentUser) || (currentUser && !userRole && isLoggingIn)}
              className="w-3/4 px-3 py-2 mt-1 border rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              style={{ 
                backgroundColor: 'var(--color-table-row-alt)', 
                color: 'var(--color-text)',
                borderColor: 'var(--color-table)'
              }}
            />
          </div>
          <button
            type="submit"
            disabled={isLoggingIn || (authLoading && !currentUser) || (currentUser && !userRole && isLoggingIn)}
            className="w-full px-4 py-2 font-bold text-white rounded-md btn-raised hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            style={{ backgroundColor: 'var(--color-primary)' }}
          >
            {isLoggingIn ? 'Logging in...' : 'Login'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default LoginPage;
